import React, { FormEventHandler, useState } from 'react'
import styled from 'styled-components/macro'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useControls } from 'src/components/async/model/control/useControls'
import { useSaveFrameworkSections } from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import type { SectionEditSelection } from 'src/pages/Frameworks/show/frameworksShowSections.types'
import { ModalForm, ModalFormId } from '../..'

const WarningList = styled.ul`
  margin: 12px 0 0;
  padding-left: 20px;
`

type Props = {
  framework: ComplianceFramework
  selectedSection: SectionEditSelection | null
  clearSelectedSection: () => void
}

export const DeleteFrameworkSectionConfirmModal = ({
  framework,
  selectedSection,
  clearSelectedSection,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM)
  const hideModal = useHideModal({
    modalId: ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM,
  })
  const saveSections = useSaveFrameworkSections()
  const cfModelId = framework.getModelId()
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeAndReset = () => {
    hideModal()
    setIsSubmitting(false)
    clearSelectedSection()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!selectedSection) return
    setIsSubmitting(true)
    try {
      await saveSections({
        complianceFrameworkModelId: cfModelId,
        originalSections: selectedSection.sectionDraftsSnapshot,
        newSections: selectedSection.sectionDraftsSnapshot.filter(
          (section) => section.tempId !== selectedSection.sectionId,
        ),
        controls: controlsData?.getItemsList() ?? [],
      })
      toast.success('Section deleted.')
      closeAndReset()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('DeleteFrameworkSectionConfirmModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM}
      size="lg"
      title="Delete section?"
      submitText="Delete section"
      disableSubmitButton={isSubmitting || controlsLoading || !selectedSection}
    >
      <form
        id={ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM}
        onSubmit={onSubmit}
      >
        {selectedSection?.sectionName ?? 'This section'} and its linked
        requirements will be permanently removed.
        <WarningList>
          <li>This action cannot be undone.</li>
          <li>All requirements in this section will be deleted.</li>
        </WarningList>
      </form>
    </ModalForm>
  )
}
